import React, { useMemo, useState } from "react";
import { Loader2, RefreshCw, UtensilsCrossed } from "lucide-react";
import ProducaoSalaoBusca from "../../../components/ProducaoSalaoBusca";
import { matchesMesaComandaSearch } from "../../../utils/matchesMesaComandaSearch";
import {
  PORCAO_COLUNAS,
  useProducaoPorcoes,
} from "../context/ProducaoPorcoesContext";
import { PorcaoStatusColuna } from "../constants/porcoes";
import PorcaoColuna from "./PorcaoColuna";
import PorcaoItemCard from "./PorcaoItemCard";

const ProducaoPorcoesContent: React.FC = () => {
  const {
    colunas,
    loading,
    refreshing,
    actionId,
    refreshNow,
    handleFinalizar,
    totalAtivos,
  } = useProducaoPorcoes();

  const [mobileTab, setMobileTab] = useState<PorcaoStatusColuna>("pending");
  const [searchQuery, setSearchQuery] = useState("");

  const colunasFiltradas = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return colunas;
    return {
      pending: colunas.pending.filter((item) =>
        matchesMesaComandaSearch(item, q),
      ),
      done: colunas.done.filter((item) => matchesMesaComandaSearch(item, q)),
    };
  }, [colunas, searchQuery]);

  if (loading) {
    return (
      <main className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <Loader2 size={32} className="animate-spin text-amber-600" />
      </main>
    );
  }

  const mobileDef = PORCAO_COLUNAS.find((d) => d.status === mobileTab)!;
  const mobileItems = colunasFiltradas[mobileTab];

  return (
    <main className="flex h-[calc(100vh-4rem)] flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 bg-white px-3 py-2.5 sm:justify-between sm:px-4 sm:py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <UtensilsCrossed
            size={20}
            className="shrink-0 text-amber-600"
          />
          <h1 className="truncate text-base font-bold text-neutral-800 sm:text-lg">
            Produção Porções
          </h1>
          <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
            {totalAtivos} {totalAtivos === 1 ? "ativo" : "ativos"}
          </span>
          <span className="hidden text-xs text-neutral-500 lg:inline">
            FoodFly · Birigui
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <ProducaoSalaoBusca value={searchQuery} onChange={setSearchQuery} />
          <button
            type="button"
            onClick={() => void refreshNow()}
            disabled={refreshing}
            aria-label="Atualizar"
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {refreshing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            <span className="hidden sm:inline">
              {refreshing ? "Atualizando..." : "Atualizar"}
            </span>
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col sm:hidden">
        <div className="flex gap-1.5 border-b border-neutral-200 bg-white px-3 py-2">
          {PORCAO_COLUNAS.map((def) => {
            const isActive = def.status === mobileTab;
            return (
              <button
                key={def.status}
                type="button"
                onClick={() => setMobileTab(def.status)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2.5 text-xs font-semibold transition-colors ${
                  isActive
                    ? "bg-neutral-800 text-white"
                    : "bg-neutral-100 text-neutral-600 active:bg-neutral-200"
                }`}
              >
                <span className="truncate">{def.title}</span>
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                    isActive ? "bg-white/20 text-white" : def.badgeClass
                  }`}
                >
                  {colunasFiltradas[def.status].length}
                </span>
              </button>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {mobileItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
              <UtensilsCrossed size={40} className="mb-2 opacity-30" />
              <p className="text-sm font-medium">
                {searchQuery.trim()
                  ? "Nenhum resultado para esta busca"
                  : `Nenhum item em ${mobileDef.title.toLowerCase()}`}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 pb-6">
              {mobileItems.map((item) => (
                <PorcaoItemCard
                  key={item.id}
                  item={item}
                  actionId={actionId}
                  onFinalizar={(id) => void handleFinalizar(id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="hidden min-h-0 flex-1 overflow-x-auto overflow-y-hidden sm:block">
        <div className="flex h-full gap-4 p-4">
          {PORCAO_COLUNAS.map((def) => (
            <PorcaoColuna
              key={def.status}
              def={def}
              items={colunasFiltradas[def.status]}
              actionId={actionId}
              onFinalizar={(id) => void handleFinalizar(id)}
            />
          ))}
        </div>
      </div>
    </main>
  );
};

export default ProducaoPorcoesContent;
